import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogo, logoLight, SignupImg } from "../../assets/svg/Index";
import { Button, InputField } from "@/components";
import { useApi } from "@/";
import { X } from "lucide-react";
import { useFieldError, useValidation, useAlert } from "@/";
import { SignupSchema } from "../../schemas";
import { useGoogleLogin } from "@react-oauth/google";

const SignUpPage = () => {
  const { alert, AlertDialog } = useAlert();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    monthlyShipments: "5000",
  });
  const [generalError, setGeneralError] = useState("");
  const navigate = useNavigate();
  const { callApi, loading } = useApi();
  const { validate } = useValidation(SignupSchema);
  const [formError, setFormError] = useState([]);
  const getErrorMessage = useFieldError(formError);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Handle Google sign-up using @react-oauth/google
  const handleGoogleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setGoogleLoading(true);
        setGeneralError("");

        // Call your backend API with the Google authorization code
        const apiResponse = await callApi("user/auth/google", "POST", {
          code: tokenResponse.code,
        });

        if (apiResponse.success) {
          const token = apiResponse.data.token;
          const refreshToken = apiResponse.data.refreshToken;
          await alert("Registration successful!");
          localStorage.setItem("token", token);
          localStorage.setItem("refreshToken", refreshToken);
          navigate("/");
        } else {
          setGeneralError(
            apiResponse.error ||
              "Google authentication failed. Please try again."
          );
        }
      } catch (error) {
        console.error("Google auth processing error:", error);
        setGeneralError(
          error.message || "Something went wrong with Google authentication."
        );
      } finally {
        setGoogleLoading(false);
      }
    },
    // Change from implicit flow to authorization code flow
    flow: "auth-code",
    onError: (error) => {
      console.error("Google login error:", error);
      setGeneralError(
        "Google sign-up failed: " +
          (error?.error_description || error?.message || "Unknown error")
      );
      setGoogleLoading(false);
    },
    onNonOAuthError: (error) => {
      console.error("Non-OAuth error:", error);
      setGeneralError(
        "Google sign-up configuration error: " +
          (error?.error_description ||
            error?.message ||
            "redirect_uri_mismatch")
      );
      setGoogleLoading(false);
    },
  });

  // Google button click handler
  const initiateGoogleSignup = () => {
    try {
      setGoogleLoading(true);
      handleGoogleSignup();
    } catch (error) {
      console.error("Failed to initiate Google signup:", error);
      setGeneralError(
        "Failed to start Google authentication. Please try again."
      );
      setGoogleLoading(false);
    }
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");

    // Validate the form data
    const result = validate(formData);
    console.log("Validation result:", result);
    if (!result.isValid) {
      setFormError(result.errors);
      return;
    }

    try {
      // Call the API to register the user
      const response = await callApi("user/register", "POST", result.data);

      // Check if the registration was successful
      if (response.success) {
        await alert(
          "To verify your account, please check your email. We've sent a verification link to your email address.Please note that it may take 10-15 minutes to receive the email."
        );
        try {
          const userEmail = formData.email;
          await callApi("common/send-email-verification", "POST", {
            email: userEmail,
          });
        } catch (error) {
          console.error("Verification failed:", error);
        }
        navigate("/login"); // Redirect to login page on success
      } else {
        // Display API-specific error message
        console.log("Registration failed:", response);
        setFormError([]);
        setGeneralError(
          response.error || "Registration failed. Please try again."
        );
      }
    } catch (error) {
      // Handle unexpected errors (e.g., network issues)
      console.error("Unexpected error during registration:", error);
      setGeneralError(
        error.message || "Something went wrong. Please try again."
      );
    }
  };

  return (
    <div className="flex flex-col sm:flex-row h-dvh w-full bg-background justify-center">
      {/* Close Button */}
      <button
        className="absolute top-4 z-10 right-4 p-2 rounded-xl bg-background_2/60 hover:bg-primary/30 transition-all"
        onClick={() => navigate("/")}
      >
        <X className="w-5 h-5 text-secondary/60" />
      </button>

      {/* Left Side */}
      <div className="hidden sm:flex w-1/2 flex-col justify-center items-start bg-background_2 p-2 relative">
        <img
          src={SignupImg}
          alt="Signup Visual"
          className="w-full h-full object-cover rounded-xl"
        />
      </div>

      {/* Right Side */}
      <div className="w-full h-full  sm:w-1/2  bg-background  overflow-y-auto ">
        <div className="min-h-[720px] w-full h-full flex flex-col justify-center gap-3 p-8 relative">
          <a href="https://indiancouriercompany.com">
            <img
              src={logoLight}
              alt="Logo"
              className="absolute top-4 left-7 w-40 opacity-90"
            />
          </a>
          <div className="max-w-lg">
            <h2 className="text-headline3 font-nunito font-semibold text-secondary">
              Welcome,
            </h2>
            <p className="text-subtitle2 font-ptsans text-secondary/65">
              Shipping Without Stress, From Your Place to Your Customer's.
            </p>
          </div>
          <form
            className="w-full flex flex-col gap-2 max-w-lg mt-4"
            onSubmit={handleSubmit}
          >
            {/* General Error Message */}
            {generalError && (
              <p className="text-red-500 mb-4">{generalError}</p>
            )}

            {/* First Name and Last Name Fields */}
            <div className="flex gap-4">
              <InputField
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={(value) => handleChange("firstName", value)}
                placeholder="First Name"
                error={getErrorMessage("firstName")}
                className="w-1/2"
                disabled={loading}
              />
              <InputField
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={(value) => handleChange("lastName", value)}
                placeholder="Last Name"
                error={getErrorMessage("lastName")}
                className="w-1/2"
                disabled={loading}
              />
            </div>

            {/* Email Field */}
            <InputField
              type="email"
              id="email"
              value={formData.email}
              onChange={(value) => handleChange("email", value)}
              placeholder="Enter your email"
              error={getErrorMessage("email")}
              disabled={loading}
            />

            {/* Password Field */}
            <InputField
              type="password"
              id="password"
              value={formData.password}
              onChange={(value) => handleChange("password", value)}
              placeholder="Enter your password"
              error={getErrorMessage("password")}
              disabled={loading}
            />

            {/* Confirm Password Field */}
            <InputField
              type="password"
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={(value) => handleChange("confirmPassword", value)}
              placeholder="Confirm your password"
              error={getErrorMessage("confirmPassword")}
              disabled={loading}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-primaryL mt-2"
              isLoading={loading}
            >
              Register
            </Button>

            {/* Divider */}
            <span className="flex justify-center items-center text-subtitle1 my-3">
              or
            </span>

            {/* Google Login Button */}
            <Button
              type="button"
              className="w-full !bg-transparent !text-secondary/80 font-nunito border-2 border-secondary/10 py-2 rounded-xl flex items-center justify-center hover:bg-secondary/5 hover:scale-[0.96] transition-all"
              onClick={initiateGoogleSignup}
              isLoading={googleLoading}
              loadingText="Please Wait..."
            >
              {!googleLoading && (
                <img src={GoogleLogo} alt="Google" className="w-7 h-7 mr-2" />
              )}
              Continue with Google
            </Button>

            {/* Login Link */}
            <div className="flex justify-center w-full mt-6">
              <p className="text-secondary/80 text-subtitle1">
                Already have an account?{" "}
                <Link to="/login" className="text-primary/80 hover:underline">
                  Log-in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
      <AlertDialog />
    </div>
  );
};

export default SignUpPage;
